/**
 * NetSuiteCustomerPicker — shared component for searching, selecting, creating,
 * and unlinking a NetSuite customer.
 *
 * Used in:
 *   - New-client onboarding modal (Clients.tsx)
 *   - Client editor (ClientEdit.tsx)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, Loader2, Search, Unlink, X } from 'lucide-react';
import { getErrorMessage } from '../../services/apiService';
import {
  getAllCustomers,
  searchCustomers,
  getCustomer,
  getCustomerAddress,
  createCustomer,
  CustomerSummary,
} from '../../services/netsuiteService';

// --------------------------------------------------------------------------
// Props
// --------------------------------------------------------------------------

export interface NetSuitePrefillFields {
  companyName?: string;
  contactEmail?: string;
  phone?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface NetSuiteCustomerPickerProps {
  /** Currently-linked NetSuite customer id. Empty string or undefined = not linked. */
  linkedId?: string;
  /** Used as the company name when "Create in NetSuite" is clicked. */
  companyName?: string;
  /** Contact email forwarded to create. */
  contactEmail?: string;
  /** Phone forwarded to create. */
  phone?: string;
  /** Disable all interactions (e.g. while an auto-save is in flight). */
  disabled?: boolean;
  /**
   * Called when a customer is linked (passes the id) or unlinked (passes null).
   */
  onLink: (id: string | null) => void;
  /**
   * Called with the picked customer's details so the parent can optionally
   * backfill empty form fields. Callers decide whether to overwrite or skip
   * fields that already have values.
   */
  onPrefill?: (fields: NetSuitePrefillFields) => void;
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

const NetSuiteCustomerPicker: React.FC<NetSuiteCustomerPickerProps> = ({
  linkedId,
  companyName = '',
  contactEmail = '',
  phone = '',
  disabled = false,
  onLink,
  onPrefill,
}) => {
  const [allCustomers, setAllCustomers] = useState<CustomerSummary[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadAllError, setLoadAllError] = useState('');

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CustomerSummary[] | null>(null);
  const [searchError, setSearchError] = useState('');

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState('');
  const [linkedName, setLinkedName] = useState('');

  // Create-confirmation modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPhone, setCreatePhone] = useState('');

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load all customers once on mount so the user can filter client-side
  useEffect(() => {
    let cancelled = false;
    setLoadingAll(true);
    getAllCustomers()
      .then((rows) => { if (!cancelled) setAllCustomers(rows); })
      .catch((err) => { if (!cancelled) setLoadAllError(getErrorMessage(err)); })
      .finally(() => { if (!cancelled) setLoadingAll(false); });
    return () => { cancelled = true; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Client-side filter on the pre-loaded list
  const filtered: CustomerSummary[] = (() => {
    if (searchResults !== null) return searchResults;
    if (!query.trim()) return allCustomers.slice(0, 50);
    const q = query.toLowerCase();
    return allCustomers
      .filter((c) =>
        (c.companyName ?? '').toLowerCase().includes(q) ||
        (c.entityId ?? '').toLowerCase().includes(q)
      )
      .slice(0, 50);
  })();

  const handleSearchNetSuite = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSearchError('');
    try {
      const results = await searchCustomers(query.trim());
      setSearchResults(results);
      setDropdownOpen(true);
    } catch (err) {
      setSearchError(getErrorMessage(err));
    } finally {
      setSearching(false);
    }
  };

  const handleSelect = async (customer: CustomerSummary) => {
    if (!customer.id) return;
    setDropdownOpen(false);
    setLinking(true);
    setActionError('');
    try {
      const [detail, address] = await Promise.all([
        getCustomer(customer.id),
        getCustomerAddress(customer.id),
      ]);

      const name = detail.companyName || detail.entityId || customer.companyName || customer.entityId || customer.id;
      setLinkedName(name ?? '');
      onLink(customer.id);

      // Build prefill object — only pass values that exist
      if (onPrefill) {
        const prefill: NetSuitePrefillFields = {};
        if (detail.companyName || detail.entityId) prefill.companyName = detail.companyName || detail.entityId;
        if (detail.email) prefill.contactEmail = detail.email;
        if (detail.phone) prefill.phone = detail.phone;
        if (address) {
          if (address.addr1) {
            prefill.streetAddress = address.addr2
              ? `${address.addr1}, ${address.addr2}`
              : address.addr1;
          }
          if (address.city) prefill.city = address.city;
          if (address.state) prefill.state = address.state;
          if (address.zip) prefill.zipCode = address.zip;
        }
        onPrefill(prefill);
      }
    } catch (err) {
      setActionError(getErrorMessage(err));
    } finally {
      setLinking(false);
    }
  };

  const openCreateModal = () => {
    setCreateName(companyName.trim());
    setCreateEmail(contactEmail.trim());
    setCreatePhone(phone.trim());
    setActionError('');
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    if (!createName.trim()) return;
    setCreating(true);
    setActionError('');
    setShowCreateModal(false);
    try {
      const created = await createCustomer({
        isPerson: false,
        companyName: createName.trim(),
        email: createEmail.trim() || undefined,
        phone: createPhone.trim() || undefined,
      });
      if (created.id) {
        const name = created.companyName || created.entityId || createName;
        setLinkedName(name ?? '');
        onLink(created.id);
      }
    } catch (err) {
      setActionError(getErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const handleUnlink = () => {
    setLinkedName('');
    setSearchResults(null);
    setQuery('');
    onLink(null);
  };

  const isLinked = Boolean(linkedId);
  const isBusy = disabled || loadingAll || linking || creating;

  return (
    <div className="rounded-xl border border-black/[0.08] bg-[#f8fafc] p-4 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Link className="w-4 h-4 text-[#c41e3a]" />
        <span className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Link NetSuite customer</span>
      </div>

      {loadAllError && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Could not pre-load customer list: {loadAllError}. Use the search button to query NetSuite directly.
        </p>
      )}

      {actionError && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{actionError}</p>
      )}

      {isLinked ? (
        /* Linked state */
        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
          <div className="text-sm text-emerald-800">
            <span className="font-semibold">Linked:</span>{' '}
            {linkedName || linkedId}{' '}
            <span className="text-emerald-600 font-mono text-xs">(#{linkedId})</span>
          </div>
          <button
            type="button"
            onClick={handleUnlink}
            disabled={isBusy}
            className="flex items-center gap-1 text-xs text-[#64748b] hover:text-red-600 transition-colors disabled:opacity-50"
          >
            <Unlink className="w-3.5 h-3.5" /> Unlink
          </button>
        </div>
      ) : (
        /* Picker UI */
        <div className="space-y-2">
          <div className="flex gap-2" ref={dropdownRef}>
            <div className="relative flex-1">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchResults(null); // revert to client-side filter
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                placeholder={loadingAll ? 'Loading customers…' : 'Search by company name or ID…'}
                disabled={isBusy}
                className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm bg-white focus:outline-none focus:border-[#c41e3a]/50 disabled:opacity-50"
              />
              {dropdownOpen && filtered.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-black/[0.1] rounded-lg shadow-xl max-h-48 overflow-y-auto">
                  {filtered.map((c, i) => (
                    <button
                      key={c.id ?? i}
                      type="button"
                      onMouseDown={() => handleSelect(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#fef2f2] hover:text-[#c41e3a] transition-colors flex items-center justify-between gap-2"
                    >
                      <span className={c.isInactive ? 'line-through text-[#94a3b8]' : ''}>
                        {c.companyName || c.entityId || c.id}
                      </span>
                      <span className="text-xs text-[#94a3b8] font-mono shrink-0">
                        {c.isInactive ? 'inactive · ' : ''}#{c.id}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleSearchNetSuite}
              disabled={!query.trim() || searching || isBusy}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#475569] hover:border-[#c41e3a]/50 disabled:opacity-50 transition-all whitespace-nowrap"
            >
              {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              Search
            </button>
          </div>

          {searchError && <p className="text-xs text-red-700">{searchError}</p>}

          {(linking || creating) && (
            <div className="flex items-center gap-2 text-xs text-[#64748b]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {linking ? 'Fetching customer details…' : 'Creating in NetSuite…'}
            </div>
          )}

          {companyName.trim() && (
            <button
              type="button"
              onClick={openCreateModal}
              disabled={isBusy}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#c41e3a]/30 bg-[#fef2f2] text-sm font-medium text-[#c41e3a] hover:bg-[#fee2e2] disabled:opacity-40 transition-all"
            >
              <Link className="w-3.5 h-3.5 shrink-0" />
              {creating ? 'Creating in NetSuite…' : 'Create New Customer in NetSuite'}
            </button>
          )}

          {/* Create-confirmation modal */}
          {showCreateModal && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreateModal(false)} />
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-[#0f172a]">Create NetSuite Customer</h3>
                  <button type="button" onClick={() => setShowCreateModal(false)} className="text-[#94a3b8] hover:text-[#475569]">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-[#64748b]">Review the details below before creating. This will create a new customer in NetSuite and link it to this client.</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-[#475569] mb-1">Company Name *</label>
                    <input
                      type="text"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-black/[0.12] text-sm bg-white focus:outline-none focus:border-[#c41e3a]/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#475569] mb-1">Email</label>
                    <input
                      type="email"
                      value={createEmail}
                      onChange={(e) => setCreateEmail(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-black/[0.12] text-sm bg-white focus:outline-none focus:border-[#c41e3a]/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#475569] mb-1">Phone</label>
                    <input
                      type="text"
                      value={createPhone}
                      onChange={(e) => setCreatePhone(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-black/[0.12] text-sm bg-white focus:outline-none focus:border-[#c41e3a]/50"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 rounded-lg border border-black/[0.12] text-sm text-[#475569] hover:bg-black/[0.03] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={!createName.trim()}
                    className="flex-1 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-medium hover:bg-[#a01830] disabled:opacity-40 transition-colors"
                  >
                    Create in NetSuite
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NetSuiteCustomerPicker;
